import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import type { Database } from '@/types/database.generated';

export type JournalTemplateRow = Database['public']['Tables']['journal_templates']['Row'];
export type ClinicType = Database['public']['Enums']['clinic_type'];
export type JournalTemplateScope = 'personal' | 'clinic';

export const journalTemplatesKey = (
  clinicId: string | null | undefined,
  patientId?: string | null,
) => ['journal', 'templates', clinicId ?? null, patientId ?? null] as const;

/**
 * Templates the current user may use: their personal templates plus clinic-wide
 * templates. RLS hides other practitioners' personal templates.
 */
export function useJournalTemplates(
  clinicId: string | null | undefined,
  _patientId?: string | null,
) {
  return useQuery({
    queryKey: journalTemplatesKey(clinicId),
    enabled: Boolean(clinicId),
    queryFn: async (): Promise<JournalTemplateRow[]> => {
      if (!clinicId) return [];

      const { data, error } = await supabase
        .from('journal_templates')
        .select('*')
        .eq('clinic_id', clinicId)
        .is('archived_at', null)
        .order('title', { ascending: true });
      if (error) throw error;
      return data ?? [];
    },
    staleTime: 30_000,
  });
}

export function useCreateJournalTemplate(clinicId: string | null | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      title: string;
      content_md?: string;
      category?: string;
      description?: string;
      vertical_type?: ClinicType | null;
      patient_id?: string | null;
      scope?: JournalTemplateScope;
    }) => {
      const { data, error } = await supabase.rpc('create_journal_template', {
        p_clinic_id: clinicId!,
        p_title: input.title,
        p_content_md: input.content_md ?? '',
        p_category: input.category ?? undefined,
        p_vertical_type: input.vertical_type ?? undefined,
        p_patient_id: input.patient_id ?? undefined,
        p_description: input.description ?? undefined,
        p_scope: input.scope ?? undefined,
      });
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      if (clinicId) void qc.invalidateQueries({ queryKey: ['journal', 'templates', clinicId] });
    },
  });
}

export function useUpdateJournalTemplate(clinicId: string | null | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      id: string;
      title?: string;
      content_md?: string;
      category?: string;
      description?: string;
      vertical_type?: ClinicType | null;
      scope?: JournalTemplateScope;
    }) => {
      const { data, error } = await supabase.rpc('update_journal_template', {
        p_id: input.id,
        p_title: input.title ?? undefined,
        p_content_md: input.content_md ?? undefined,
        p_category: input.category ?? undefined,
        p_vertical_type: input.vertical_type ?? undefined,
        p_description: input.description ?? undefined,
        p_scope: input.scope ?? undefined,
      });
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      if (clinicId) void qc.invalidateQueries({ queryKey: ['journal', 'templates', clinicId] });
    },
  });
}

export function useArchiveJournalTemplate(clinicId: string | null | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { data, error } = await supabase.rpc('archive_journal_template', { p_id: id });
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      if (clinicId) void qc.invalidateQueries({ queryKey: ['journal', 'templates', clinicId] });
    },
  });
}
